import { useEffect, useCallback } from 'react';
import { useIntl } from 'react-intl';

import { noop } from 'ui-components/default-props/noop';
import { useAsyncSafeState } from 'ui-components/hooks/async-safe';
import {
  NotificationService,
  NOTIFICATION_TYPE_ERROR,
  NOTIFICATION_TYPE_SUCCESS,
  NOTIFICATION_DURATIONS,
} from 'ui-components/services/notification-service';

import messages from './messages';
import { Features } from '../../../../constants/features';
import { useIsSellerFeatureEnabled } from '../../../../hooks/use-is-seller-feature-enabled';
import { LogService } from '../../../../services/log-service';
import {
  getSellerV1ApiKeys,
  deleteSellerV1ApiKey,
  generateSellerV1ApiKey,
  deleteV2SellerApiKey,
  generateV2SellerApiKeys,
  getV2SellerApiKeys,
} from '../../api/api-keys';
import { checkIsErrorMessageUserCancelledMfa } from '../../../../global/step-up-authentication/helpers';

/**
 * @typedef {object} useApiKeysResult
 * @property {[import('../types/api-keys').APIKeysV1]} v1ApiKeys
 * @property {import('../types/api-keys').APIKeysV2} v2ProductionApiKey
 * @property {boolean} isRemovingApiKey
 * @property {boolean} isCreatingApiKeys
 * @property {(apiKeyId: number) => Promise<void>} deleteV1ApiKey
 * @property {() => Promise<import('../types/api-keys').APIKeysV1>} generateV1ApiKeys
 * @property {(isSandboxKey: boolean) => Promise<void>} createV2ApiKey
 * @property {(props) => Promise<void>} deleteV2ApiKey
 * @returns {useApiKeysResult}
 */
export const useApiKeys = () => {
  const intl = useIntl();

  const isSellerFeatureApiV2Enabled = useIsSellerFeatureEnabled(Features.ShipStationApiV2);

  const [v2ProductionApiKey, setV2ProductionApiKey] = useAsyncSafeState();
  const [isCreatingApiKeys, setIsCreatingApiKeys] = useAsyncSafeState(false);
  const [isRemovingApiKey, setIsRemovingApiKey] = useAsyncSafeState(false);
  const [v1ApiKeys, setV1ApiKeys] = useAsyncSafeState([]);

  const fetchKeysV1 = useCallback(async () => {
    /**
     * Empty `.catch` to prevent fake errors for the user
     * Backend returns 404/500 when the user does not have an API key created
     */
    await getSellerV1ApiKeys()
      .then((apiKeys) => setV1ApiKeys(apiKeys))
      .catch(noop);
  }, [setV1ApiKeys]);

  /**
   * useEffect to get the V2 API keys
   * Empty `.catch` to prevent fake errors for the user
   * Backend returns 404/500 when the user does not have an API key created
   */
  useEffect(() => {
    if (!isSellerFeatureApiV2Enabled) {
      return;
    }

    const fetchV2Keys = async () => {
      await getV2SellerApiKeys()
        .then((v1ProdApiKeys) => {
          setV2ProductionApiKey(v1ProdApiKeys.filter((k) => !k.isSandboxKey)[0]);
        })
        .catch(noop);
    };
    fetchV2Keys();
  }, [isSellerFeatureApiV2Enabled, setV2ProductionApiKey]);

  /**
   * useEffect to get the V1 API keys
   */
  useEffect(() => {
    fetchKeysV1();
  }, [fetchKeysV1]);

  /**
   * @param {boolean} isSandboxKey
   */
  const createV2ApiKey = async (isSandboxKey) => {
    setIsCreatingApiKeys(true);

    await generateV2SellerApiKeys()
      .then((productionApiKey) => {
        setV2ProductionApiKey(productionApiKey);

        NotificationService.showNotification({
          title: intl.formatMessage(messages.successTitle),
          notificationType: NOTIFICATION_TYPE_SUCCESS,
          content: intl.formatMessage(messages.generateSuccessMessage),
          duration: NOTIFICATION_DURATIONS.SHORT,
        });
      })
      .catch((error) => {
        if (!checkIsErrorMessageUserCancelledMfa(error)) {
          LogService.logError(error);
        }

        NotificationService.showNotification({
          title: intl.formatMessage(messages.generateErrorTitle, {
            isSandboxKey,
          }),
          notificationType: NOTIFICATION_TYPE_ERROR,
          content: intl.formatMessage(messages.generateErrorMessage, {
            isSandboxKey,
          }),
          duration: NOTIFICATION_DURATIONS.SHORT,
        });
      })
      .finally(() => setIsCreatingApiKeys(false));
  };

  /**
   * @param {object} props
   * @param {string} props.encryptedApiKey
   * @param {boolean} props.isSandboxKey
   */
  const deleteV2ApiKey = async ({ encryptedApiKey, isSandboxKey }) => {
    setIsRemovingApiKey(true);

    await deleteV2SellerApiKey(encryptedApiKey)
      .then(() => {
        setV2ProductionApiKey(null);

        NotificationService.showNotification({
          title: intl.formatMessage(messages.successTitle),
          notificationType: NOTIFICATION_TYPE_SUCCESS,
          content: intl.formatMessage(messages.deleteSuccessMessage, {
            isSandboxKey,
          }),
          duration: NOTIFICATION_DURATIONS.SHORT,
        });
      })
      .catch((error) => {
        if (!checkIsErrorMessageUserCancelledMfa(error)) {
          LogService.logError(error);
        }
        NotificationService.showNotification({
          title: intl.formatMessage(messages.deleteErrorTitle, {
            isSandboxKey,
          }),
          notificationType: NOTIFICATION_TYPE_ERROR,
          content: intl.formatMessage(messages.deleteErrorMessage, {
            isSandboxKey,
          }),
          duration: NOTIFICATION_DURATIONS.SHORT,
        });
      })
      .finally(() => setIsRemovingApiKey(false));
  };

  /**
   * @param {number} apiKeyId
   */
  const deleteV1ApiKey = async (apiKeyId) => {
    setIsRemovingApiKey(true);

    await deleteSellerV1ApiKey(apiKeyId)
      .then(async () => {
        await fetchKeysV1();

        NotificationService.showNotification({
          title: intl.formatMessage(messages.successTitle),
          notificationType: NOTIFICATION_TYPE_SUCCESS,
          content: intl.formatMessage(messages.deleteSuccessMessageV1),
          duration: NOTIFICATION_DURATIONS.SHORT,
        });
      })
      .catch((error) => {
        if (!checkIsErrorMessageUserCancelledMfa(error)) {
          LogService.logError(error);
        }
        NotificationService.showNotification({
          title: intl.formatMessage(messages.deleteErrorTitleV1),
          notificationType: NOTIFICATION_TYPE_ERROR,
          content: intl.formatMessage(messages.deleteErrorMessageV1),
          duration: NOTIFICATION_DURATIONS.SHORT,
        });
      })
      .finally(() => setIsRemovingApiKey(false));
  };

  /**
   * Created a new API Keys V1
   * @param {string} expiration
   * @returns {Promise<import('../types/api-keys').APIKeysV1>}
   */
  const generateV1ApiKeys = async (expiration) => {
    setIsCreatingApiKeys(true);

    return generateSellerV1ApiKey({ expiryDateTime: expiration })
      .then((createdApiKey) => {
        fetchKeysV1();
        return createdApiKey;
      })
      .catch((error) => {
        if (!checkIsErrorMessageUserCancelledMfa(error)) {
          LogService.logError(error);
        }
        NotificationService.showNotification({
          title: intl.formatMessage(messages.generateFailTitle),
          notificationType: NOTIFICATION_TYPE_ERROR,
          content: intl.formatMessage(messages.generateFailMessage),
          duration: NOTIFICATION_DURATIONS.SHORT,
        });
      })
      .finally(() => setIsCreatingApiKeys(false));
  };

  return {
    v1ApiKeys,
    v2ProductionApiKey,
    isRemovingApiKey,
    isCreatingApiKeys,
    deleteV1ApiKey,
    generateV1ApiKeys,
    createV2ApiKey,
    deleteV2ApiKey,
  };
};
